// 牌桌背景（程序化 — PRD §15 S3.2 重構策略）
// 對應 PRD §4.4 CGameScene 版面、§15 S3.2 重構建議
//
// === 使用圖片 ===
// 無 — 純 Phaser.Graphics 程序化繪製，零 asset 依賴。
// 桌燈光線層為執行期以 Canvas 2D 繪製的 CanvasTexture（key: TABLE_LIGHT_KEY），不需載入檔案。
// （CBootScene 仍會載入 table-a.png，但已未使用；之後可從 public/assets/ui/ 移除）
//
// Stage 0 過渡實作（鏡像合成 table-a.png）已棄用 — 重構為：
//   1. 漸層底色（Graphics.fillGradientStyle，純程式繪製，零 asset）
//   2. 裝飾邊框（Graphics.strokeRect 兩層線條 + 四角小方塊）
//   3. （可選 noise tile：MVP 暫不加，視覺已足夠）
//
// 為什麼好處：
// - 零 asset：不依賴 table-a.png（現可從 public/ 移除而本檔不受影響）
// - 任何解析度都銳利（向量化）
// - 換主題只改顏色常數
// - GPU 記憶體 ~70 KB（漸層 + 邊框 graphics buffer）vs 原 table-a 整張 ~1.6 MB

import type Phaser from 'phaser';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';

import { UIDepth } from './ui-depths';

/** 桌面顏色主題（之後可外移成 i18n / theme manager）。 */
export const TABLE_THEME = {
  /** 漸層頂部色 — 偏亮的綠 */
  gradientTop: 0x2a8c3a,
  /** 漸層底部色 — 偏暗的綠 */
  gradientBottom: 0x0a3d2e,
  /** 裝飾邊框色 — 暖金 */
  borderColor: 0xc9a647,
  /** 邊框透明度 */
  borderAlpha: 0.85,
} as const;

// ============================================================================
// 版面常數（尺寸 / depth 微調都改這裡）
// ============================================================================

const GRADIENT_ALPHA = 1; // 漸層底色 alpha（1 = 完全不透明）
const BORDER_LINE_WIDTH = 2; // 裝飾邊框線條粗細
const BORDER_OUTER_INSET = 30; // 外框距畫面四邊的內縮
const BORDER_INNER_INSET_DELTA = 8; // 內框相對外框再內縮（雙線視覺）
const BORDER_CORNER_SIZE = 16; // 四角裝飾小方塊邊長
const GRADIENT_DEPTH = UIDepth.TableGradient; // 漸層底層（負值代表在所有 UI 之下）
const BORDER_DEPTH = UIDepth.TableBorder; // 邊框（高於漸層、仍在所有 UI 之下）
const LIGHT_DEPTH = UIDepth.TableLight; // 桌燈光線（蓋在漸層與邊框上、仍在牌與風字之下）

// ---- 桌燈光線（中央暖光 + 四角暗角；只畫在桌面層，不壓暗牌面） ----
export const TABLE_LIGHT_KEY = 'table-light'; // 執行期生成的光線 CanvasTexture key
const LIGHT_CENTER_X = LOGICAL_WIDTH / 2; // 暖光中心 X（牌桌中央）
const LIGHT_CENTER_Y = 330; // 暖光中心 Y（略高於畫面中心，對齊海底區）
const LIGHT_RADIUS = 560; // 暖光擴散半徑
const LIGHT_MID_STOP = 0.55; // 暖光衰減中段位置（0~1）
const LIGHT_COLOR_CENTER = 'rgba(255,236,190,0.22)'; // 暖光中心色
const LIGHT_COLOR_MID = 'rgba(255,236,190,0.07)'; // 暖光中段色
const LIGHT_COLOR_EDGE = 'rgba(255,236,190,0)'; // 暖光外緣（全透明）
const VIGNETTE_INNER_RADIUS = 300; // 暗角起始半徑（此範圍內不壓暗）
const VIGNETTE_OUTER_RADIUS = 820; // 暗角最暗處半徑（約到畫面四角）
const VIGNETTE_COLOR_INNER = 'rgba(0,0,0,0)'; // 暗角內圈（全透明）
const VIGNETTE_COLOR_OUTER = 'rgba(0,12,6,0.55)'; // 暗角外圈（偏墨綠的黑）

/** 生成（或沿用已存在的）桌燈光線 texture；整個 game 只畫一次，各 scene 共用。 */
const ensureTableLightTexture = (scene: Phaser.Scene, w: number, h: number): void => {
  if (scene.textures.exists(TABLE_LIGHT_KEY)) return;
  const texture = scene.textures.createCanvas(TABLE_LIGHT_KEY, w, h);
  const ctx = texture?.getContext();
  if (!texture || !ctx) return;

  const light = ctx.createRadialGradient(
    LIGHT_CENTER_X,
    LIGHT_CENTER_Y,
    0,
    LIGHT_CENTER_X,
    LIGHT_CENTER_Y,
    LIGHT_RADIUS
  );
  light.addColorStop(0, LIGHT_COLOR_CENTER);
  light.addColorStop(LIGHT_MID_STOP, LIGHT_COLOR_MID);
  light.addColorStop(1, LIGHT_COLOR_EDGE);
  ctx.fillStyle = light;
  ctx.fillRect(0, 0, w, h);

  const vignette = ctx.createRadialGradient(
    w / 2,
    h / 2,
    VIGNETTE_INNER_RADIUS,
    w / 2,
    h / 2,
    VIGNETTE_OUTER_RADIUS
  );
  vignette.addColorStop(0, VIGNETTE_COLOR_INNER);
  vignette.addColorStop(1, VIGNETTE_COLOR_OUTER);
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, w, h);

  texture.refresh();
};

/**
 * 在指定 Scene 鋪設桌面背景（漸層 + 裝飾邊框）。
 *
 * 兩層皆為 Graphics 物件、不持狀態，呼叫端可進一步調整 depth。
 *
 * @returns [漸層底, 邊框] — 通常呼叫端只需把它們設置在最低 depth
 */
export const createTableBackground = (
  scene: Phaser.Scene
): [Phaser.GameObjects.Graphics, Phaser.GameObjects.Graphics] => {
  const w = LOGICAL_WIDTH;
  const h = LOGICAL_HEIGHT;

  // ============ 漸層底色 ============
  // fillGradientStyle 接 4 個角色（左上/右上/左下/右下）
  // 上下亮暗對比模擬桌燈打光的視覺效果
  const gradient = scene.add.graphics();
  gradient.fillGradientStyle(
    TABLE_THEME.gradientTop,
    TABLE_THEME.gradientTop,
    TABLE_THEME.gradientBottom,
    TABLE_THEME.gradientBottom,
    GRADIENT_ALPHA
  );
  gradient.fillRect(0, 0, w, h);

  // ============ 裝飾邊框 ============
  const border = scene.add.graphics();
  border.lineStyle(BORDER_LINE_WIDTH, TABLE_THEME.borderColor, TABLE_THEME.borderAlpha);

  // 外框
  const inset = BORDER_OUTER_INSET;
  border.strokeRect(inset, inset, w - inset * 2, h - inset * 2);

  // 內框（再內縮 BORDER_INNER_INSET_DELTA，雙線視覺）
  const innerInset = inset + BORDER_INNER_INSET_DELTA;
  border.strokeRect(innerInset, innerInset, w - innerInset * 2, h - innerInset * 2);

  // 四角裝飾小方塊
  const corners: ReadonlyArray<readonly [number, number]> = [
    [inset, inset],
    [w - inset - BORDER_CORNER_SIZE, inset],
    [inset, h - inset - BORDER_CORNER_SIZE],
    [w - inset - BORDER_CORNER_SIZE, h - inset - BORDER_CORNER_SIZE],
  ];
  for (const [cx, cy] of corners) {
    border.strokeRect(cx, cy, BORDER_CORNER_SIZE, BORDER_CORNER_SIZE);
  }

  // 兩個圖層都送到最底（不擋其他 UI）
  gradient.setDepth(GRADIENT_DEPTH);
  border.setDepth(BORDER_DEPTH);

  // ============ 桌燈光線（中央暖光 + 四角暗角） ============
  ensureTableLightTexture(scene, w, h);
  if (scene.textures.exists(TABLE_LIGHT_KEY)) {
    scene.add.image(0, 0, TABLE_LIGHT_KEY).setOrigin(0).setDepth(LIGHT_DEPTH);
  }

  return [gradient, border];
};
